import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ConversationAiAnalysis, Scorecard } from '@tips-crm/shared';
import { rerunConversationAnalysis } from '../api/endpoints.js';

const GRADE_BADGE: Record<string, string> = {
  excellent: 'badge--sentiment-pos',
  good: 'badge--sentiment-pos',
  fair: 'badge--sentiment-mixed',
  poor: 'badge--sentiment-neg',
};

function ScoreBars({ scorecard }: { scorecard: Scorecard }) {
  const { t } = useTranslation();
  const dims: Array<{ key: keyof Scorecard & string; label: string }> = [
    { key: 'discovery', label: t('callAi.scoreDiscovery') },
    { key: 'objection_handling', label: t('callAi.scoreObjectionHandling') },
    { key: 'closing', label: t('callAi.scoreClosing') },
    { key: 'next_steps_clarity', label: t('callAi.scoreNextSteps') },
  ];
  return (
    <div className="score-bars">
      {dims.map(({ key, label }) => {
        const value = Number(scorecard[key] ?? 0);
        return (
          <div key={key} className="score-bar">
            <span className="score-bar__label">{label}</span>
            <span className="score-bar__track">
              <span
                className={`score-bar__fill${value <= 2 ? ' score-bar__fill--low' : ''}`}
                style={{ width: `${(value / 5) * 100}%` }}
              />
            </span>
            <span className="score-bar__value">{value}/5</span>
          </div>
        );
      })}
      {scorecard.comment && <p className="muted score-bars__comment">{scorecard.comment}</p>}
    </div>
  );
}

function formatMs(ms: number): string {
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
}

/**
 * AI analysis of a completed call: summary, suggested disposition (+ apply),
 * competitors, objections w/ handling grades, coaching scorecard, talk metrics,
 * and the carry-forward prep note. Polls until the worker produces an analysis.
 */
export function CallAnalysisCard({
  conversationId,
  analysis,
  loading = false,
  conversationStatus,
  currentDisposition,
  onApplyDisposition,
  applying,
}: {
  conversationId: string;
  analysis: ConversationAiAnalysis | undefined;
  loading?: boolean;
  conversationStatus?: string;
  currentDisposition?: string | null;
  onApplyDisposition?: (disposition: string) => void;
  applying?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const rerun = useMutation({
    mutationFn: () => rerunConversationAnalysis(conversationId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['conversation-analysis', conversationId] });
    },
  });

  const a = analysis;

  // Nothing to show for calls that are still live and have no analysis yet.
  if (!a && conversationStatus && conversationStatus !== 'completed') return null;

  return (
    <section className="card ai-analysis">
      <h3 className="card__title">
        {t('callAi.title')}
        {a?.input_kind === 'notes_only' && (
          <span className="badge badge--note">{t('callAi.notesOnly')}</span>
        )}
      </h3>

      {!a && <p className="muted">{loading ? t('callAi.loading') : t('callAi.pending')}</p>}

      {a && a.status === 'failed' && (
        <p className="muted">
          {t('callAi.failed')}
          {a.error ? ` — ${a.error}` : ''}
        </p>
      )}
      {a && (a.status === 'pending' || a.status === 'running') && (
        <p className="muted">{t('callAi.running')}</p>
      )}
      {a && a.status === 'skipped' && <p className="muted">{t('callAi.skipped')}</p>}

      {a && a.status === 'completed' && (
        <>
          {a.summary && <p className="ai-analysis__summary">{a.summary}</p>}

          {a.disposition_suggested && (
            <div className="ai-analysis__row">
              <span className="muted">{t('callAi.suggestedDisposition')}:</span>{' '}
              <span className="badge badge--note">
                {a.disposition_suggested.replace(/_/g, ' ')}
                {a.disposition_confidence != null &&
                  ` · ${Math.round(a.disposition_confidence * 100)}%`}
              </span>
              {a.disposition_applied && (
                <span className="badge">{t('callAi.appliedByAi')}</span>
              )}
              {onApplyDisposition &&
                currentDisposition !== a.disposition_suggested && (
                  <button
                    type="button"
                    className="btn btn--small"
                    disabled={applying}
                    onClick={() => onApplyDisposition(a.disposition_suggested!)}
                  >
                    {t('callAi.apply')}
                  </button>
                )}
              {a.disposition_rationale && (
                <p className="muted ai-analysis__rationale">{a.disposition_rationale}</p>
              )}
            </div>
          )}

          {a.competitors.length > 0 && (
            <div className="ai-analysis__row">
              <span className="muted">{t('callAi.competitors')}:</span>{' '}
              {a.competitors.map((c) => (
                <span key={c.name} className="badge badge--chat" title={c.context}>
                  {c.name}
                </span>
              ))}
            </div>
          )}

          {a.objections.length > 0 && (
            <div className="ai-analysis__row">
              <h4 className="ai-analysis__subtitle">{t('callAi.objections')}</h4>
              <ul className="ai-analysis__objections">
                {a.objections.map((o, i) => (
                  <li key={i}>
                    <span className={`badge badge--sentiment ${GRADE_BADGE[o.handling_grade] ?? ''}`}>
                      {t(`callAi.grade.${o.handling_grade}`)}
                    </span>{' '}
                    <strong>{o.objection}</strong>
                    {o.rep_response && <span className="muted"> — {o.rep_response}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {a.scorecard && (
            <div className="ai-analysis__row">
              <h4 className="ai-analysis__subtitle">{t('callAi.scorecard')}</h4>
              <ScoreBars scorecard={a.scorecard} />
            </div>
          )}

          {a.next_attempt_note && (
            <div className="ai-analysis__prep">
              <h4 className="ai-analysis__subtitle">{t('callAi.prepNote')}</h4>
              <p>{a.next_attempt_note}</p>
            </div>
          )}
        </>
      )}

      {/* Talk metrics are deterministic (from Transcribe Call Analytics) — show them
          even while the LLM analysis is pending or disabled. */}
      {a?.talk_metrics && (
        <div className="ai-analysis__row ai-analysis__metrics muted">
          {a.talk_metrics.talk_ratio != null && (
            <span>
              {t('callAi.talkRatio')}: {Math.round(a.talk_metrics.talk_ratio * 100)}%
            </span>
          )}
          {a.talk_metrics.longest_agent_monologue_ms != null && (
            <span>
              {t('callAi.longestMonologue')}: {formatMs(a.talk_metrics.longest_agent_monologue_ms)}
            </span>
          )}
          {a.talk_metrics.interruptions_agent != null && (
            <span>
              {t('callAi.interruptions')}: {a.talk_metrics.interruptions_agent}
            </span>
          )}
          {a.talk_metrics.agent_question_count != null && (
            <span>
              {t('callAi.questionsAsked')}: {a.talk_metrics.agent_question_count}
            </span>
          )}
        </div>
      )}

      {a && a.status !== 'running' && (
        <button
          type="button"
          className="btn btn--small ai-analysis__rerun"
          disabled={rerun.isPending}
          onClick={() => rerun.mutate()}
        >
          {rerun.isPending ? t('callAi.rerunning') : t('callAi.rerun')}
        </button>
      )}
    </section>
  );
}
